import type { SupabaseClient } from '@supabase/supabase-js'

type RecoveryAuth = SupabaseClient['auth']
export type RecoveryGate = { status: 'ready' | 'mfa' | 'blocked'; factors: { id: string; label: string }[]; message: string }
const unavailable = 'Your account requires multi-factor verification, but no supported verified authenticator is available. Contact LobeWork support for help recovering access. A new reset link will not bypass MFA.'

export async function inspectRecoveryMfa(auth: RecoveryAuth): Promise<RecoveryGate> {
  const assurance = await auth.mfa.getAuthenticatorAssuranceLevel()
  const factors = await auth.mfa.listFactors()
  if (assurance.error || factors.error) throw new Error('Unable to check account verification. Please retry before updating your password.')
  const verified = factors.data.all.filter(factor => factor.status === 'verified')
  const totp = verified.filter(factor => factor.factor_type === 'totp').map((factor, totpIndex) => ({ id: factor.id, label: `Authenticator app ${totpIndex + 1}` }))
  if (assurance.data.currentLevel === 'aal2') return { status: 'ready', factors: [], message: '' }
  if (assurance.data.nextLevel === 'aal2' || verified.length > 0) {
    return totp.length ? { status: 'mfa', factors: totp, message: '' } : { status: 'blocked', factors: [], message: unavailable }
  }
  if (assurance.data.currentLevel !== 'aal1') throw new Error('Your session has expired. Request a new password reset.')
  return { status: 'ready', factors: [], message: '' }
}

export async function verifyRecoveryMfa(auth: RecoveryAuth, factorId: string, code: string): Promise<RecoveryGate> {
  const gate = await inspectRecoveryMfa(auth)
  if (gate.status === 'ready') return gate
  if (gate.status === 'blocked') throw new Error(gate.message)
  if (!gate.factors.some(factor => factor.id === factorId)) throw new Error('Choose a verified authenticator to continue.')
  if (!/^\d{6}$/.test(code)) throw new Error('Enter the six-digit code from your authenticator app.')
  const result = await auth.mfa.challengeAndVerify({ factorId, code })
  if (result.error) throw new Error('Verification failed. Check your authenticator code and try again.')
  const assurance = await auth.mfa.getAuthenticatorAssuranceLevel()
  if (assurance.error || assurance.data.currentLevel !== 'aal2') throw new Error('Verification could not be confirmed. Please retry before updating your password.')
  return { status: 'ready', factors: [], message: '' }
}
